import { useState, useEffect, useCallback } from 'react'
import { getBiographies, type Biography, type GetBiographiesParams } from '@/api/biography'

export interface UseBiographiesOptions {
  autoFetch?: boolean // 是否自动获取数据，默认 true
  initialParams?: GetBiographiesParams // 初始参数
}

export interface UseBiographiesReturn {
  biographies: Biography[]
  isLoading: boolean
  error: Error | null
  total: number
  fetchBiographies: (params?: GetBiographiesParams) => Promise<void>
  refetch: () => Promise<void>
}

/**
 * 获取传记列表的自定义 Hook
 * @param options 配置选项
 * @returns 传记列表、加载状态和相关操作方法
 */
export const useBiographies = (options: UseBiographiesOptions = {}): UseBiographiesReturn => {
  const { autoFetch = true, initialParams = { page: 1, page_size: 50 } } = options

  const [biographies, setBiographies] = useState<Biography[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [total, setTotal] = useState(0)
  const [currentParams, setCurrentParams] = useState<GetBiographiesParams>(initialParams)

  const fetchBiographies = useCallback(async (params?: GetBiographiesParams) => {
    const fetchParams = params || currentParams
    setCurrentParams(fetchParams)
    setIsLoading(true)
    setError(null)

    try {
      const response = await getBiographies(fetchParams)
      if (response?.items) {
        setBiographies(response.items)
        setTotal(response.meta?.total || 0)
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch biographies')
      setError(error)
      console.error('Failed to fetch biographies:', error)
    } finally {
      setIsLoading(false)
    }
  }, [currentParams])

  const refetch = useCallback(() => {
    return fetchBiographies(currentParams)
  }, [fetchBiographies, currentParams])

  useEffect(() => {
    if (autoFetch) {
      fetchBiographies(initialParams)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFetch])

  return {
    biographies,
    isLoading,
    error,
    total,
    fetchBiographies,
    refetch
  }
}

